import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Images, Upload, Loader2, X, Sparkles, Trash2, Pencil, Link2, CheckCircle2, MoreVertical, Search, ChevronDown, Tag as TagIcon, CheckSquare, Square, Download, FolderDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import AlbumManager from '../components/AlbumManager';

const fileToDataUrl = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
const inputCls = 'w-full px-2 py-1.5 rounded border text-sm';
const border = { borderColor: 'rgba(0,0,0,0.2)' };

// Post-upload review: edit proposed metadata for each new image before it is applied to EXIF + file name.
function ReviewModal({ items, onChange, onApply, onSkip, busy, mediaAbsUrl }) {
  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="gallery-review-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <div>
            <h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>Review image details — {items.length} image{items.length !== 1 ? 's' : ''}</h3>
            <p className="text-xs" style={{ color: 'var(--olive)' }}>Adjust the proposed title, file name, tags and description. Applying writes them into each file's EXIF and name.</p>
          </div>
          <button onClick={onSkip} data-testid="gallery-review-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          {items.map((it, i) => (
            <div key={it.id} className="grid grid-cols-[96px_1fr] gap-3 pb-4 border-b last:border-b-0" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`gallery-review-item-${it.id}`}>
              <img src={mediaAbsUrl(it.url)} alt="" className="w-24 h-24 object-cover rounded border" style={{ borderColor: 'rgba(0,0,0,0.15)' }} />
              <div className="space-y-1.5">
                <div className="grid grid-cols-2 gap-2">
                  <input value={it.title || ''} onChange={(e) => onChange(i, 'title', e.target.value)} placeholder="Title" data-testid={`gallery-review-title-${i}`} className={inputCls} style={border} />
                  <input value={it.filename || ''} onChange={(e) => onChange(i, 'filename', e.target.value)} placeholder="File name" data-testid={`gallery-review-filename-${i}`} className={inputCls} style={border} />
                </div>
                <input value={it.tags || ''} onChange={(e) => onChange(i, 'tags', e.target.value)} placeholder="Tags, comma-separated" data-testid={`gallery-review-tags-${i}`} className={inputCls} style={border} />
                <textarea value={it.description || ''} onChange={(e) => onChange(i, 'description', e.target.value)} rows={2} placeholder="Description" data-testid={`gallery-review-description-${i}`} className={inputCls} style={border} />
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <button onClick={onSkip} disabled={busy} data-testid="gallery-review-skip" className="px-4 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}>Keep values</button>
          <button onClick={onApply} disabled={busy} data-testid="gallery-review-apply" className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>{busy && <Loader2 size={14} className="animate-spin" />} Apply to EXIF &amp; file names</button>
        </div>
      </div>
    </div>
  );
}

// Compact dropdown multi-select for tags.
function TagDropdown({ tags, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }; window.addEventListener('mousedown', h); return () => window.removeEventListener('mousedown', h); }, []);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="gallery-tag-dropdown-btn"
        className="inline-flex items-center gap-1 px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)', color: 'var(--ink)' }}>
        <TagIcon size={14} /> {selected.length ? `${selected.length} tag${selected.length > 1 ? 's' : ''}` : 'Filter by tags'} <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute z-[60] mt-1 w-56 max-h-72 overflow-auto bg-white rounded-lg shadow-xl border py-1" style={{ borderColor: 'rgba(0,0,0,0.12)' }} data-testid="gallery-tag-dropdown-panel">
          {selected.length > 0 && <button onClick={onClear} data-testid="gallery-tag-dropdown-clear" className="w-full text-left px-3 py-1.5 text-xs" style={{ color: 'var(--maroon)' }}>Clear tag filter</button>}
          {tags.length === 0 && <div className="px-3 py-2 text-xs" style={{ color: 'var(--olive)' }}>No tags yet.</div>}
          {tags.map((t) => { const on = selected.includes(t); return (
            <button key={t} onClick={() => onToggle(t)} data-testid={`gallery-tag-opt-${t}`} className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-black/5 text-left">
              {on ? <CheckSquare size={14} style={{ color: 'var(--maroon)' }} /> : <Square size={14} style={{ color: 'rgba(0,0,0,0.35)' }} />} {t}
            </button>
          ); })}
        </div>
      )}
    </div>
  );
}

const searchBlob = (m) => [m.title, m.description, m.filename, m.detected_text, m.people, m.place, m.album, m.member_name, ...(m.tags || [])].filter(Boolean).join(' ').toLowerCase();

export default function GalleryEditorTab() {
  const auth = useAuth();
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [selTags, setSelTags] = useState([]);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | inuse | unused
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState([]); // selected image ids (bulk)
  const [menu, setMenu] = useState(null); // {x,y,item}
  const [manageAlbums, setManageAlbums] = useState(false);
  const [detail, setDetail] = useState(null); // item opened via double-click
  const [edit, setEdit] = useState(null); // item being edited
  const [deps, setDeps] = useState(null); // {item, usages, loading}
  const [bulkTag, setBulkTag] = useState(null); // {mode:'add'|'remove', value:''}
  const [progress, setProgress] = useState(null); // {done,total,failed}
  const [review, setReview] = useState(null); // uploaded items awaiting detail review
  const [dragOver, setDragOver] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    auth.adminMediaList(selTags, 'any')
      .then((r) => setItems(Array.isArray(r) ? r : []))
      .catch(() => { setItems([]); toast.error('Could not load images. Please try again.'); })
      .finally(() => setLoading(false));
  }, [auth, selTags]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { auth.adminMediaTags().then((d) => setTags(d.tags || [])).catch(() => {}); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const c = () => setMenu(null); window.addEventListener('click', c); return () => window.removeEventListener('click', c); }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (menu) setMenu(null);
      else if (bulkTag) setBulkTag(null);
      else if (review) setReview(null);
      else if (edit) setEdit(null);
      else if (deps) setDeps(null);
      else if (detail) setDetail(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu, bulkTag, review, edit, deps, detail]);

  const term = q.trim().toLowerCase();
  const shown = items.filter((m) => {
    if (filter !== 'all' && (filter === 'inuse' ? !m.in_use : m.in_use)) return false;
    if (term && !searchBlob(m).includes(term)) return false;
    return true;
  });
  const filtersActive = filter !== 'all' || selTags.length > 0 || !!term;
  const clearFilters = () => { setFilter('all'); setSelTags([]); setQ(''); };

  const isSel = (id) => sel.includes(id);
  const toggleSel = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const shownIds = shown.map((m) => m.id);
  const selShown = sel.filter((id) => shownIds.includes(id)); // only currently-visible selections count / are acted on
  const allShownSelected = shownIds.length > 0 && shownIds.every((id) => sel.includes(id));
  const toggleSelectAll = () => setSel(allShownSelected ? sel.filter((id) => !shownIds.includes(id)) : Array.from(new Set([...sel, ...shownIds])));
  const selItems = () => shown.filter((m) => sel.includes(m.id)); // scoped to visible set (never act on hidden images)

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) { toast.error('Please choose image files.'); return; }
    setBusy(true); setProgress({ done: 0, total: files.length, failed: 0 });
    const uploaded = [];
    for (const f of files) {
      try { const d = await fileToDataUrl(f); const r = await auth.adminMediaUpload(d); if (r && r.item) uploaded.push(r.item); }
      catch { setProgress((p) => ({ ...p, failed: p.failed + 1 })); }
      finally { setProgress((p) => ({ ...p, done: p.done + 1 })); }
    }
    setBusy(false); setProgress(null); load();
    if (!uploaded.length) { toast.error('Upload failed. Please try different images.'); return; }
    toast.success(`${uploaded.length} of ${files.length} image${files.length !== 1 ? 's' : ''} uploaded and analysed.`);
    setReview(uploaded.map((it) => ({ ...it, tags: (it.tags || []).join(', '), _orig: it })));
  };
  const doUpload = async (e) => { await uploadFiles(e.target.files); e.target.value = ''; };
  const onDrop = async (e) => { e.preventDefault(); setDragOver(false); if (!busy) await uploadFiles(e.dataTransfer.files); };
  const onDragOver = (e) => { e.preventDefault(); if (!dragOver) setDragOver(true); };

  const reviewChange = (i, k, v) => setReview((r) => r.map((it, j) => j === i ? { ...it, [k]: v } : it));
  const applyReview = async () => {
    setBusy(true); let n = 0;
    for (const it of review) {
      const tags = (it.tags || '').split(',').map((s) => s.trim()).filter(Boolean);
      const o = it._orig;
      const changed = it.title !== o.title || it.filename !== o.filename || it.description !== o.description || tags.join(',') !== (o.tags || []).join(',');
      if (!changed) continue;
      try { await auth.adminMediaUpdate(it.id, { title: it.title, description: it.description, filename: it.filename, tags }); n += 1; } catch { /* skip */ }
    }
    setBusy(false); setReview(null); load();
    toast.success(n ? `Applied your edits to ${n} image${n !== 1 ? 's' : ''} (EXIF + file name).` : 'Values kept — already written to EXIF.');
  };

  const download = async (item) => {
    setMenu(null);
    try { await auth.adminMediaDownload(item.id); } catch { toast.error('Download failed. Please try again.'); }
  };
  const bulkDownload = async () => {
    const list = selItems(); if (!list.length) return;
    setBusy(true);
    try { await auth.adminMediaDownloadZip(list.map((m) => m.id)); toast.success(`Preparing ${list.length} image(s) as a zip.`); }
    catch { toast.error('Download failed. Please try again.'); }
    finally { setBusy(false); }
  };

  const openMenuAt = (item, x, y) => setMenu({ x, y, item });
  const onContext = (e, item) => { e.preventDefault(); openMenuAt(item, e.clientX, e.clientY); };
  const onKebab = (e, item) => { e.preventDefault(); e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); openMenuAt(item, r.right, r.bottom); };

  const openEdit = (it) => { setEdit({ ...it, tags: (it.tags || []).join(', '), _origFilename: it.filename }); setMenu(null); setDetail(null); };

  const regen = async (item) => {
    setMenu(null); setBusy(true);
    try { const r = await auth.adminMediaRegen(item.id); toast.success('Image details regenerated.'); if (detail) setDetail(r.item || detail); load(); }
    catch { toast.error('Regeneration failed. Please try again.'); }
    finally { setBusy(false); }
  };

  const showDeps = async (item) => {
    setMenu(null);
    setDeps({ item, usages: null, loading: true });
    try { const u = await auth.adminMediaUsage(item.id); setDeps({ item, usages: u.usages || [], loading: false }); }
    catch { setDeps({ item, usages: [], loading: false, error: true }); }
  };

  const del = async (item) => {
    setMenu(null);
    try {
      await auth.adminMediaDelete(item.id, false);
      toast.success('Image deleted.'); setDetail(null); load();
    } catch (e2) {
      const d = e2.response && e2.response.data && e2.response.data.detail;
      const usages = (d && d.usages) || [];
      if (usages.length) {
        if (window.confirm(`This image is used in ${usages.length} place(s):\n` + usages.map((x) => `• ${x.type}: ${x.name}`).join('\n') + `\n\nDelete anyway? Links there will break.`)) {
          try { await auth.adminMediaDelete(item.id, true); toast.success('Image deleted.'); setDetail(null); load(); }
          catch { toast.error('Delete failed. Please try again.'); }
        }
      } else { toast.error('Delete failed. Please try again.'); }
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    const renamed = edit.filename !== edit._origFilename;
    try {
      await auth.adminMediaUpdate(edit.id, { title: edit.title, description: edit.description, filename: edit.filename, tags: (edit.tags || '').split(',').map((s) => s.trim()).filter(Boolean) });
      if (renamed) {
        const u = await auth.adminMediaUsage(edit.id);
        if (u.usages && u.usages.length) toast.info(`Renamed. Links use a permanent ID, so the ${u.usages.length} place(s) using this image keep working automatically.`);
      }
      toast.success('Saved and EXIF applied.');
      setEdit(null); load();
    } catch { toast.error('Could not save changes. Please try again.'); }
    finally { setBusy(false); }
  };

  // ---- Bulk actions ----
  const bulkRegen = async () => {
    const list = selItems(); if (!list.length) return;
    setBusy(true); let ok = 0;
    for (const it of list) { try { await auth.adminMediaRegen(it.id); ok += 1; } catch { /* continue */ } }
    setBusy(false); setSel([]); toast.success(`Regenerated details for ${ok}/${list.length} image(s).`); load();
  };

  const bulkDelete = async () => {
    const list = selItems(); if (!list.length) return;
    const inUse = list.filter((m) => m.in_use);
    let force = false;
    if (inUse.length && !window.confirm(`${inUse.length} of ${list.length} selected image(s) are currently in use. Deleting them will break those links.\n\nClick OK to delete ALL selected (including in-use), or Cancel to skip.`)) {
      // delete only the unused ones
    } else if (inUse.length) { force = true; }
    if (!list.length) return;
    setBusy(true); let ok = 0;
    for (const it of list) {
      if (it.in_use && !force) continue;
      try { await auth.adminMediaDelete(it.id, it.in_use ? true : false); ok += 1; }
      catch { /* skip */ }
    }
    setBusy(false); setSel([]); toast[ok ? 'success' : 'info'](ok ? `Deleted ${ok} image(s).` : 'No images deleted.'); load();
  };

  const applyBulkTags = async () => {
    const raw = (bulkTag.value || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (!raw.length) { setBulkTag(null); return; }
    const list = selItems(); setBusy(true); let ok = 0;
    for (const it of list) {
      const cur = (it.tags || []).map((t) => t.toLowerCase());
      const next = bulkTag.mode === 'add'
        ? Array.from(new Set([...cur, ...raw]))
        : cur.filter((t) => !raw.includes(t));
      try { await auth.adminMediaUpdate(it.id, { tags: next }); ok += 1; } catch { /* skip */ }
    }
    setBusy(false); setBulkTag(null); setSel([]);
    toast.success(`${bulkTag.mode === 'add' ? 'Added' : 'Removed'} tags on ${ok} image(s).`); load();
  };

  const MENU_ACTIONS = [
    ['Edit / EXIF', Pencil, openEdit, 'ctx-edit'],
    ['Regenerate details', Sparkles, regen, 'ctx-regen'],
    ['Download', Download, download, 'ctx-download'],
    ['Dependencies', Link2, showDeps, 'ctx-deps'],
    ['Delete', Trash2, del, 'ctx-delete'],
  ];

  return (
    <div className="max-w-6xl relative" data-testid="gallery-editor-panel" onDragOver={onDragOver} onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }} onDrop={onDrop}>
      {dragOver && (
        <div className="absolute inset-0 z-40 flex items-center justify-center rounded-lg pointer-events-none" style={{ background: 'rgba(122,86,44,0.12)', border: '3px dashed var(--maroon)' }} data-testid="gallery-drop-overlay">
          <span className="px-4 py-2 rounded font-title text-sm bg-white shadow" style={{ color: 'var(--maroon)' }}><Upload size={14} className="inline mr-1" /> Drop images to upload</span>
        </div>
      )}
      <div className="flex items-center gap-2 mb-1"><Images size={18} style={{ color: 'var(--maroon)' }} /><h2 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Gallery editor</h2>
        <button onClick={() => setManageAlbums(true)} className="ml-auto inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)', background: 'white' }} data-testid="gallery-editor-manage-albums"><FolderDown size={12} /> Manage albums</button></div>
      {manageAlbums && <AlbumManager onClose={() => setManageAlbums(false)} />}
      <p className="text-sm mb-3" style={{ color: 'var(--olive)' }}>Upload one or many images (or drag &amp; drop them anywhere here); each is analysed and its details written into EXIF. Select images with their checkbox to act on many at once. Double-click an image to open it; right-click (or tap ⋮) for single-image actions.</p>

      {/* Top action bar: upload + bulk actions */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <label className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title cursor-pointer" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="gallery-editor-upload">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {progress ? `Uploading ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…` : 'Upload images'}
          <input type="file" accept="image/*" multiple onChange={doUpload} className="hidden" data-testid="gallery-editor-file" disabled={busy} />
        </label>
        {progress && <span className="text-xs" data-testid="gallery-upload-progress" style={{ color: 'var(--navy)' }}>{progress.done}/{progress.total} done · analysing (about 30s each){progress.failed ? ` · ${progress.failed} failed` : ''}</span>}
        <button onClick={toggleSelectAll} data-testid="gallery-select-all" className="inline-flex items-center gap-1 px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
          {allShownSelected ? <CheckSquare size={14} /> : <Square size={14} />} {allShownSelected ? 'Deselect all' : 'Select all'}
        </button>
        <span className="text-sm" data-testid="gallery-selection-count" style={{ color: 'var(--navy)' }}>{selShown.length} selected</span>
        <div className="h-6 w-px" style={{ background: 'rgba(0,0,0,0.15)' }} />
        <button onClick={bulkRegen} disabled={!selShown.length || busy} data-testid="gallery-bulk-regen" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title disabled:opacity-40" style={{ background: 'var(--navy)', color: 'var(--cream)' }}><Sparkles size={14} /> Regenerate details</button>
        <button onClick={() => setBulkTag({ mode: 'add', value: '' })} disabled={!selShown.length || busy} data-testid="gallery-bulk-tag" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title disabled:opacity-40" style={{ background: 'var(--olive)', color: 'var(--cream)' }}><TagIcon size={14} /> Tag</button>
        <button onClick={bulkDownload} disabled={!selShown.length || busy} data-testid="gallery-bulk-download" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title disabled:opacity-40 border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }}><FolderDown size={14} /> Download zip</button>
        <button onClick={bulkDelete} disabled={!selShown.length || busy} data-testid="gallery-bulk-delete" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title disabled:opacity-40" style={{ background: '#8a1f11', color: '#fff' }}><Trash2 size={14} /> Delete</button>
      </div>

      {/* Filters: search + tag dropdown + in-use */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--olive)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, description, tags, text…" data-testid="gallery-editor-search"
            className="pl-8 pr-3 py-2 rounded border text-sm w-72 max-w-full" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
        </div>
        <TagDropdown tags={tags} selected={selTags} onToggle={(t) => setSelTags((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t])} onClear={() => setSelTags([])} />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="gallery-editor-filter" className="text-sm px-2 py-2 rounded border" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
          <option value="all">All images</option><option value="inuse">In use</option><option value="unused">Unused</option>
        </select>
        {filtersActive && <button onClick={clearFilters} data-testid="gallery-clear-filters" className="text-xs px-2 py-1 rounded border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }}>Clear filters</button>}
        <span className="text-xs" style={{ color: 'var(--olive)' }}>{shown.length} image{shown.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? <div className="text-center py-10"><Loader2 className="animate-spin inline" size={20} /></div>
      : shown.length === 0 ? (
        <div className="text-sm py-8 text-center" style={{ color: 'var(--olive)' }} data-testid="gallery-empty">
          {filtersActive ? <>No images match these filters. <button onClick={clearFilters} className="underline" style={{ color: 'var(--maroon)' }}>Clear filters</button></> : 'No images yet — upload one to get started.'}
        </div>
      )
      : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3" data-testid="gallery-editor-grid">
          {shown.map((m) => (
            <div key={m.id} onDoubleClick={() => setDetail(m)} onContextMenu={(e) => onContext(e, m)} data-testid={`gallery-editor-item-${m.id}`}
              className="relative rounded overflow-hidden border cursor-pointer group" style={{ borderColor: isSel(m.id) ? 'var(--navy)' : m.in_use ? '#1a7f37' : 'rgba(0,0,0,0.15)', borderWidth: isSel(m.id) || m.in_use ? 2 : 1 }} title="Double-click to open · right-click for actions">
              <img src={auth.mediaAbsUrl(m.url)} alt={m.title || ''} className="w-full h-24 object-cover" loading="lazy" />
              <label onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()} className="absolute top-1 left-1 bg-white/90 rounded p-0.5 flex cursor-pointer">
                <input type="checkbox" checked={isSel(m.id)} onChange={() => toggleSel(m.id)} data-testid={`gallery-check-${m.id}`} className="w-3.5 h-3.5 cursor-pointer" />
              </label>
              <span className="absolute bottom-6 left-1 text-[9px] px-1.5 py-0.5 rounded-full font-title inline-flex items-center gap-0.5"
                style={{ background: m.in_use ? '#1a7f37' : 'rgba(0,0,0,0.55)', color: '#fff' }} data-testid={`gallery-usage-${m.id}`}>
                {m.in_use ? <><CheckCircle2 size={9} /> in use {m.usage_count}</> : 'unused'}
              </span>
              <button onClick={(e) => onKebab(e, m)} onDoubleClick={(e) => e.stopPropagation()} data-testid={`gallery-actions-${m.id}`} title="Actions"
                className="absolute top-1 right-1 p-1 rounded-full bg-black/55 text-white opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                <MoreVertical size={13} />
              </button>
              <div className="px-1 py-0.5 text-[10px] truncate" style={{ color: 'var(--navy)' }}>{m.title || m.filename || 'untitled'}</div>
            </div>
          ))}
        </div>
      )}

      {/* Right-click / kebab context menu */}
      {menu && (
        <div className="fixed z-[70] bg-white rounded-lg shadow-xl border py-1 text-sm" style={{ left: Math.min(menu.x, window.innerWidth - 200), top: Math.min(menu.y, window.innerHeight - 220), borderColor: 'rgba(0,0,0,0.1)' }} data-testid="gallery-context-menu">
          {MENU_ACTIONS.map(([label, Icon, fn, tid]) => (
            <button key={label} onClick={() => fn(menu.item)} data-testid={tid} className="flex items-center gap-2 w-full px-4 py-1.5 hover:bg-black/5 text-left" style={{ color: label === 'Delete' ? '#8a1f11' : 'var(--ink)' }}><Icon size={14} /> {label}</button>
          ))}
        </div>
      )}

      {/* Double-click detail view with function menu */}
      {detail && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setDetail(null)} data-testid="gallery-detail-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h3 className="font-title text-lg truncate" style={{ color: 'var(--maroon)' }}>{detail.title || detail.filename || 'Image'}</h3>
              <button onClick={() => setDetail(null)} data-testid="gallery-detail-close"><X size={18} /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 p-5">
              <img src={auth.mediaAbsUrl(detail.url)} alt={detail.title || ''} className="w-full rounded border object-contain max-h-[60vh]" style={{ borderColor: 'rgba(0,0,0,0.12)' }} />
              <div className="text-sm space-y-2" style={{ color: 'var(--ink)' }}>
                <div><span className="font-title" style={{ color: 'var(--navy)' }}>File:</span> {detail.filename || '—'}</div>
                <div><span className="font-title" style={{ color: 'var(--navy)' }}>Description:</span> {detail.description || '—'}</div>
                <div className="flex flex-wrap gap-1">{(detail.tags || []).map((t) => <span key={t} className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--sage)' }}>#{t}</span>)}</div>
                {detail.detected_text ? <div><span className="font-title" style={{ color: 'var(--navy)' }}>Text in image:</span> {detail.detected_text}</div> : null}
                <div className="text-xs" style={{ color: 'var(--olive)' }}>{detail.in_use ? `In use in ${detail.usage_count} place(s)` : 'Not used anywhere'}</div>
                <div className="flex flex-wrap gap-2 pt-2">
                  <button onClick={() => openEdit(detail)} data-testid="detail-edit" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}><Pencil size={14} /> Edit / EXIF</button>
                  <button onClick={() => regen(detail)} disabled={busy} data-testid="detail-regen" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title disabled:opacity-40" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Regenerate details</button>
                  <button onClick={() => download(detail)} data-testid="detail-download" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }}><Download size={14} /> Download</button>
                  <button onClick={() => showDeps(detail)} data-testid="detail-deps" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--olive)', color: 'var(--cream)' }}><Link2 size={14} /> Dependencies</button>
                  <button onClick={() => del(detail)} data-testid="detail-delete" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title" style={{ background: '#8a1f11', color: '#fff' }}><Trash2 size={14} /> Delete</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Post-upload review */}
      {review && <ReviewModal items={review} onChange={reviewChange} onApply={applyReview} onSkip={() => setReview(null)} busy={busy} mediaAbsUrl={auth.mediaAbsUrl} />}

      {/* Edit / EXIF modal */}
      {edit && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="gallery-edit-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}><h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>Edit image & EXIF</h3><button onClick={() => setEdit(null)}><X size={18} /></button></div>
            <div className="p-5 space-y-2">
              <img src={auth.mediaAbsUrl(edit.url)} alt="" className="w-28 h-28 object-cover rounded border mb-1" style={{ borderColor: 'rgba(0,0,0,0.15)' }} />
              {[['title', 'Title'], ['filename', 'File name'], ['tags', 'Tags (comma-separated)']].map(([k, lbl]) => (
                <div key={k}><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>{lbl}</label><input value={edit[k] || ''} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`gallery-edit-${k}`} className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></div>
              ))}
              <div><label className="text-xs font-title" style={{ color: 'var(--navy)' }}>Description</label><textarea value={edit.description || ''} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={3} data-testid="gallery-edit-description" className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <button onClick={() => setEdit(null)} className="px-4 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}>Cancel</button>
              <button onClick={saveEdit} disabled={busy} data-testid="gallery-edit-save" className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>{busy && <Loader2 size={14} className="animate-spin" />} Save & apply EXIF</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk tag modal */}
      {bulkTag && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="gallery-bulk-tag-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}><h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>Tag {selShown.length} image(s)</h3><button onClick={() => setBulkTag(null)}><X size={18} /></button></div>
            <div className="p-5 space-y-3">
              <div className="flex gap-2">
                <button onClick={() => setBulkTag({ ...bulkTag, mode: 'add' })} data-testid="gallery-bulk-tag-add" className="px-3 py-1.5 rounded text-sm font-title" style={{ background: bulkTag.mode === 'add' ? 'var(--maroon)' : 'var(--sage)', color: bulkTag.mode === 'add' ? 'var(--cream)' : 'var(--ink)' }}>Add tags</button>
                <button onClick={() => setBulkTag({ ...bulkTag, mode: 'remove' })} data-testid="gallery-bulk-tag-remove" className="px-3 py-1.5 rounded text-sm font-title" style={{ background: bulkTag.mode === 'remove' ? 'var(--maroon)' : 'var(--sage)', color: bulkTag.mode === 'remove' ? 'var(--cream)' : 'var(--ink)' }}>Remove tags</button>
              </div>
              <input value={bulkTag.value} onChange={(e) => setBulkTag({ ...bulkTag, value: e.target.value })} placeholder="tags, comma-separated" data-testid="gallery-bulk-tag-input" className="w-full px-2 py-1.5 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
              <div className="flex flex-wrap gap-1">{tags.slice(0, 20).map((t) => <button key={t} onClick={() => setBulkTag({ ...bulkTag, value: bulkTag.value ? `${bulkTag.value}, ${t}` : t })} className="text-xs px-2 py-0.5 rounded-full border" style={{ borderColor: 'var(--maroon)', color: 'var(--maroon)' }}>{t}</button>)}</div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <button onClick={() => setBulkTag(null)} className="px-4 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}>Cancel</button>
              <button onClick={applyBulkTags} disabled={busy} data-testid="gallery-bulk-tag-save" className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>{busy && <Loader2 size={14} className="animate-spin" />} Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Dependencies modal */}
      {deps && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="gallery-deps-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}><h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>Where this image is used</h3><button onClick={() => setDeps(null)}><X size={18} /></button></div>
            <div className="p-5">
              {deps.loading ? (
                <p className="text-sm inline-flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 size={15} className="animate-spin" /> Scanning where this image is used…</p>
              ) : deps.error ? (
                <p className="text-sm" style={{ color: '#8a1f11' }}>Could not check dependencies. Please try again.</p>
              ) : (!deps.usages || deps.usages.length === 0) ? (
                <p className="text-sm inline-flex items-center gap-2" style={{ color: '#1a7f37' }}><CheckCircle2 size={15} /> Not used anywhere — safe to delete.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {deps.usages.map((u, i) => <li key={i} className="flex items-center gap-2"><span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--sage)' }}>{u.type}</span><span style={{ color: 'var(--navy)' }}>{u.name}</span></li>)}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
